import React from 'react';
import { Platform, StatusBar } from 'react-native';
import queryString from 'query-string';
import HeaderLayout, { HeaderBindingValue } from './HeaderLayout';
import { processStyles } from '../util.style';
import HeaderConfig from './config';
import {
  getComponentScreenDimensions,
  getScreenTypeFromId,
} from '../../../utils/responsive';
import { getValueBinding } from '../../shared';

// Get status bar height for native platforms
const getStatusBarHeight = () => {
  if (Platform.OS === 'web') {
    return 0;
  }
  if (Platform.OS === 'android') {
    return StatusBar.currentHeight || 24;
  }

  return 60;
};

interface HeaderProps {
  attributes?: any;
  style?: any;
  libraryComponentManifest?: any;
  onMenuPress?: () => void;
  onMailPress?: () => void;
  onNotificationPress?: () => void;
  onHelpPress?: () => void;
  onUserPress?: () => void;
  id: string;
  width?: number;
  height?: number;
  [key: string]: any;
}

export default function Header(props: HeaderProps) {
  const isCanvas =
    Platform.OS === 'web' &&
    !queryString.parse(window?.location?.search)?.target;

  const bindingValue = getValueBinding(
    props.id,
    props.data,
    props
  ) as HeaderBindingValue;

  if (isCanvas) {
    const {
      attributes,
      style,
      libraryComponentManifest,
      id,
      width,
      height,
      ...otherProps
    } = props;

    const screenType = getScreenTypeFromId(id);
    const breakpointIndex = id?.endsWith('.0') ? 0 : id?.endsWith('.1') ? 1 : 2;
    const screenDimensions = getComponentScreenDimensions(
      HeaderConfig,
      breakpointIndex
    );

    const { finalStyle } = processStyles({
      attributes,
      style,
      config: HeaderConfig,
      libraryComponentManifest,
      id,
      ...otherProps,
    });

    const effectiveWidth = width || screenDimensions.width;
    const effectiveHeight =
      height || finalStyle.height || screenDimensions.height;

    const containerStyle = {
      backgroundColor: finalStyle.backgroundColor || '#f5f5f5',
      height: effectiveHeight,
      paddingHorizontal: finalStyle.paddingHorizontal || 16,
      zIndex: finalStyle.zIndex || 1000,
      width: effectiveWidth,
    };

    return (
      <HeaderLayout
        bindingValue={bindingValue}
        containerStyle={containerStyle}
        screenType={screenType}
        screenSize={{ width: effectiveWidth, height: effectiveHeight }}
        width={effectiveWidth}
        height={effectiveHeight}
        isCanvasMode={true}
        attributes={attributes}
        onPress={otherProps.onPress}
        actions={otherProps.actions}
        {...otherProps}
        {...props}
      />
    );
  }

  const { finalStyle } = processStyles({
    attributes: props.attributes,
    style: props.style,
    config: HeaderConfig,
    libraryComponentManifest: props.libraryComponentManifest,
    ...props,
  });

  // Only add safe area padding if header is positioned at top (top is 0 or undefined)
  const isAtTop =
    finalStyle.top === undefined ||
    finalStyle.top === 0 ||
    finalStyle.top === '0';
  const isFixed = finalStyle?.position === 'fixed';
  const shouldAddSafeAreaPadding = isAtTop && isFixed && Platform.OS !== 'web';

  const containerStyle = {
    backgroundColor: finalStyle.backgroundColor || '#f5f5f5',
    height: finalStyle.height || 64,
    paddingHorizontal: finalStyle.paddingHorizontal || 16,
    paddingTop: shouldAddSafeAreaPadding ? getStatusBarHeight() : 0, // Add status bar height only if at top
    zIndex: finalStyle.zIndex || 1000,
    ...(finalStyle.width && { width: finalStyle.width }),
  };

  return (
    <HeaderLayout
      bindingValue={bindingValue}
      {...props}
      containerStyle={containerStyle}
      isCanvasMode={false}
    />
  );
}
